import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowsDownUp, CaretUp, Check, CheckCircle, PencilSimple, Trash, X, XCircle, } from "@phosphor-icons/react";
const TRACKER_DB_NAME = "SlideTrackerLive";
const SNAPSHOT_STORE = "snapshots";
const DECK_STATE_STORE = "deckStates";
const openTrackerDb = async () => new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
    }
    const request = indexedDB.open(TRACKER_DB_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
});
const getAllDeckStates = async (db) => new Promise((resolve) => {
    try {
        const tx = db.transaction([DECK_STATE_STORE], "readonly");
        const store = tx.objectStore(DECK_STATE_STORE);
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result ?? []);
        request.onerror = () => resolve([]);
    }
    catch {
        resolve([]);
    }
});
const getSnapshot = async (db, documentId, slideId) => new Promise((resolve) => {
    try {
        const tx = db.transaction([SNAPSHOT_STORE], "readonly");
        const store = tx.objectStore(SNAPSHOT_STORE);
        const request = store.get([documentId, slideId]);
        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => resolve(null);
    }
    catch {
        resolve(null);
    }
});
const hydrateOperationsFromIndexedDb = async (operations) => {
    const db = await openTrackerDb();
    if (!db)
        return operations;
    try {
        const deckStates = await getAllDeckStates(db);
        if (deckStates.length === 0)
            return operations;
        const hydrated = [];
        for (const operation of operations) {
            let resolvedCurrentIndex = operation.currentIndex > 0
                ? operation.currentIndex
                : operation.operation.slideIndex && operation.operation.slideIndex > 0
                    ? operation.operation.slideIndex
                    : 0;
            if (operation.slideImage && operation.slideTitle) {
                hydrated.push({ ...operation, currentIndex: resolvedCurrentIndex });
                continue;
            }
            let snapshot = null;
            for (const deck of deckStates) {
                const candidateIds = [];
                if (operation.resolvedSlideId)
                    candidateIds.push(operation.resolvedSlideId);
                if (operation.operation.slideId)
                    candidateIds.push(operation.operation.slideId);
                const deckSlideIds = Array.isArray(deck.slideIds) ? deck.slideIds : [];
                const byIndex = operation.currentIndex > 0 ? deckSlideIds[operation.currentIndex - 1] : undefined;
                if (byIndex)
                    candidateIds.push(byIndex);
                const uniqueIds = [...new Set(candidateIds.filter(Boolean))];
                if (resolvedCurrentIndex <= 0) {
                    for (const candidateId of uniqueIds) {
                        const idx = deckSlideIds.indexOf(candidateId);
                        if (idx >= 0) {
                            resolvedCurrentIndex = idx + 1;
                            break;
                        }
                    }
                }
                for (const slideId of uniqueIds) {
                    snapshot = await getSnapshot(db, deck.documentId, slideId);
                    if (snapshot)
                        break;
                }
                if (snapshot)
                    break;
            }
            hydrated.push({
                ...operation,
                currentIndex: resolvedCurrentIndex,
                slideImage: operation.slideImage ?? snapshot?.imageBase64,
                slideTitle: operation.slideTitle ?? snapshot?.shortDescription,
            });
        }
        return hydrated;
    }
    finally {
        db.close();
    }
};
const hydrateOperationsFromPowerPoint = async (operations) => {
    const ppt = window.PowerPoint;
    if (!ppt?.run)
        return operations;
    const pendingIndexes = [
        ...new Set(operations
            .filter((op) => !op.slideImage && op.currentIndex > 0)
            .map((op) => op.currentIndex)),
    ];
    if (pendingIndexes.length === 0)
        return operations;
    try {
        const capturedByIndex = new Map();
        await ppt.run(async (context) => {
            const captureResults = [];
            for (const index of pendingIndexes) {
                try {
                    const slide = context.presentation.slides.getItemAt(index - 1);
                    const imageResult = slide.getImageAsBase64();
                    captureResults.push({ index, result: imageResult });
                }
                catch {
                    // Ignore out-of-range slides
                }
            }
            await context.sync();
            for (const item of captureResults) {
                if (item.result?.value) {
                    capturedByIndex.set(item.index, item.result.value);
                }
            }
        });
        return operations.map((op) => ({
            ...op,
            slideImage: op.slideImage ?? capturedByIndex.get(op.currentIndex),
        }));
    }
    catch {
        return operations;
    }
};
const getInitialDecisions = (operations) => {
    const map = new Map();
    for (const operation of operations) {
        if (operation.requiresApproval && typeof operation.approved === "boolean") {
            map.set(operation.index, operation.approved);
        }
    }
    return map;
};
const getRowStatus = (operation, decision, status) => {
    if (operation.operation.action === "move")
        return "auto-approved";
    if (typeof decision === "boolean")
        return decision ? "approved" : "rejected";
    if (typeof operation.approved === "boolean")
        return operation.approved ? "approved" : "rejected";
    if (status === "approved")
        return "approved";
    return "needs-approval";
};
const OperationRow = ({ operation, decision, rowStatus, isCollapsed, isDisabled, onToggleCollapse, onApprove, onReject, onResetDecision, }) => {
    const [imageAspectRatio, setImageAspectRatio] = useState(16 / 9);
    const isMove = operation.operation.action === "move";
    const isDelete = operation.operation.action === "delete";
    const isHighlighted = rowStatus === "needs-approval" && !isDisabled;
    const currentIndexValue = operation.currentIndex > 0
        ? operation.currentIndex
        : operation.operation.slideIndex && operation.operation.slideIndex > 0
            ? operation.operation.slideIndex
            : undefined;
    const currentSlideLabel = currentIndexValue
        ? `Slide ${currentIndexValue}`
        : operation.operation.slideId
            ? `Slide ${operation.operation.slideId}`
            : "Slide";
    const statusLabel = rowStatus === "auto-approved"
        ? "Auto-Approved"
        : rowStatus === "needs-approval"
            ? "Needs Approval"
            : rowStatus === "approved"
                ? "Approved"
                : "Rejected";
    const statusColorClass = rowStatus === "needs-approval" ? "text-gray-900" : "text-gray-600";
    return (_jsxs("div", { className: [
            "rounded-md border bg-white transition-all",
            isHighlighted ? "border-2 border-[#a9a1ed] px-[6px] py-[6px]" : "border-gray-300 p-[8px]",
        ].join(" "), children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: isHighlighted ? "text-gray-900" : "text-gray-600", children: isMove ? _jsx(ArrowsDownUp, { size: 16 }) : _jsx(Trash, { size: 16 }) }), _jsxs("p", { className: `text-[12px] leading-[1.5] font-bold ${isHighlighted ? "text-gray-900" : "text-gray-600"}`, children: [isMove ? "Move" : "Remove", " ", currentSlideLabel] }), _jsxs("div", { className: "ml-auto inline-flex items-center gap-1", children: [rowStatus === "approved" && _jsx(Check, { size: 16, className: "text-[#2acb37]" }), rowStatus === "rejected" && _jsx(X, { size: 16, className: "text-gray-600" }), _jsx("span", { className: `text-[12px] leading-[1.5] ${statusColorClass}`, children: statusLabel }), !isDisabled && isDelete && typeof decision === "boolean" && (_jsx("button", { type: "button", onClick: onResetDecision, className: "ml-1 rounded-[3px] bg-[#f8f7ff] px-1 py-0.5 text-[10px] leading-[1.5] font-bold text-[#6050d7] hover:bg-[#efecff]", children: "Change Decision" })), _jsx("button", { type: "button", onClick: onToggleCollapse, className: "ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100", "aria-label": isCollapsed ? "Expand operation details" : "Collapse operation details", children: _jsx(CaretUp, { size: 16, className: isCollapsed ? "rotate-180 text-gray-600" : "text-gray-600" }) })] })] }), !isCollapsed && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mt-2 flex items-start gap-3", children: [_jsx("div", { className: "relative w-[142px] shrink-0 overflow-hidden rounded-lg border border-gray-300 bg-gray-100", style: { aspectRatio: imageAspectRatio }, children: operation.slideImage ? (_jsx("img", { src: `data:image/png;base64,${operation.slideImage}`, alt: `Slide ${operation.currentIndex}`, className: "h-full w-full object-contain", onLoad: (e) => {
                                        const target = e.currentTarget;
                                        if (target.naturalWidth > 0 && target.naturalHeight > 0) {
                                            setImageAspectRatio(target.naturalWidth / target.naturalHeight);
                                        }
                                    } })) : (_jsx("div", { className: "flex h-full w-full items-center justify-center text-[11px] text-gray-500", children: "No preview" })) }), _jsxs("div", { className: "flex min-w-0 flex-1 flex-col justify-center gap-1", children: [_jsx("p", { className: "truncate text-[12px] leading-[1.5] font-bold text-gray-600", children: operation.slideTitle ?? `Slide ${operation.currentIndex}` }), _jsxs("div", { className: "flex items-center gap-2 rounded bg-gray-100 p-1", children: [isMove && operation.operation.newIndex ? (_jsxs("div", { className: "inline-flex shrink-0 items-center gap-1 whitespace-nowrap", children: [_jsx("span", { className: "inline-flex items-center rounded border border-gray-300 bg-gray-100 px-[5px] py-1 text-[11px] leading-4 text-gray-900", children: currentIndexValue ?? "?" }), _jsx(ArrowRight, { size: 12, className: "shrink-0 text-gray-600" }), _jsx("span", { className: "inline-flex items-center rounded border border-gray-300 bg-gray-100 px-[5px] py-1 text-[11px] leading-4 text-gray-900", children: operation.operation.newIndex })] })) : (_jsx("span", { className: "inline-flex items-center rounded border border-gray-300 bg-gray-100 px-[5px] py-1 text-[11px] leading-4 text-gray-900", children: operation.currentIndex })), _jsx("p", { className: "text-[11px] leading-[1.5] text-gray-600", children: operation.operation.reason ??
                                                    (isMove
                                                        ? `Move ${currentSlideLabel} to position ${operation.operation.newIndex ?? "?"}`
                                                        : `Delete ${currentSlideLabel}`) })] })] })] }), isDelete && !isDisabled && rowStatus === "needs-approval" && (_jsxs("div", { className: "mt-2 flex items-center justify-end gap-1", children: [_jsxs("button", { type: "button", onClick: onApprove, className: "inline-flex items-center gap-1 rounded-md border border-gray-600 bg-white px-2.5 py-[3.5px] text-[11px] leading-[1.5] font-bold text-gray-800 hover:bg-gray-50", children: ["Approve", _jsx(CheckCircle, { size: 16, className: "text-gray-800" })] }), _jsxs("button", { type: "button", onClick: onReject, className: "inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-[3.5px] text-[11px] leading-[1.5] font-bold text-gray-800 hover:bg-gray-50", children: ["Reject", _jsx(XCircle, { size: 16, className: "text-gray-800" })] })] }))] }))] }));
};
export const SlideOperationsApprovalBlock = ({ data, onSubmit, onCancel, }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [collapsedRows, setCollapsedRows] = useState(new Set());
    const [approvalDecisions, setApprovalDecisions] = useState(() => getInitialDecisions(data.operations));
    const [hydratedOperations, setHydratedOperations] = useState(data.operations);
    useEffect(() => {
        setApprovalDecisions(getInitialDecisions(data.operations));
        setHydratedOperations(data.operations);
    }, [data.id, data.operations]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const fromIndexedDb = await hydrateOperationsFromIndexedDb(data.operations);
            const withPowerPointFallback = await hydrateOperationsFromPowerPoint(fromIndexedDb);
            if (!cancelled)
                setHydratedOperations(withPowerPointFallback);
        })();
        return () => {
            cancelled = true;
        };
    }, [data.id, data.operations]);
    const isDisabled = data.status !== "pending";
    const stats = useMemo(() => {
        const moves = hydratedOperations.filter((op) => op.operation.action === "move").length;
        const deletions = hydratedOperations.filter((op) => op.operation.action === "delete").length;
        const approved = Array.from(approvalDecisions.values()).filter(Boolean).length;
        const rejected = Array.from(approvalDecisions.values()).filter((v) => !v).length;
        const pendingDeletions = Math.max(0, deletions - approved - rejected);
        return { moves, deletions, approved, rejected, pendingDeletions };
    }, [approvalDecisions, hydratedOperations]);
    const allDeletionsDecided = stats.pendingDeletions === 0;
    const handleApprove = useCallback((operationIndex) => {
        setApprovalDecisions((prev) => {
            const next = new Map(prev);
            next.set(operationIndex, true);
            return next;
        });
    }, []);
    const handleReject = useCallback((operationIndex) => {
        setApprovalDecisions((prev) => {
            const next = new Map(prev);
            next.set(operationIndex, false);
            return next;
        });
    }, []);
    const handleResetDecision = useCallback((operationIndex) => {
        setApprovalDecisions((prev) => {
            const next = new Map(prev);
            next.delete(operationIndex);
            return next;
        });
    }, []);
    const handleSubmit = useCallback(() => {
        if (isDisabled || !allDeletionsDecided)
            return;
        onSubmit(approvalDecisions);
    }, [allDeletionsDecided, approvalDecisions, isDisabled, onSubmit]);
    const statusMessage = data.status === "approved"
        ? "Operations executed."
        : data.status === "timeout"
            ? "Approval timed out. No changes were made."
            : data.status === "cancelled"
                ? "Cancelled. No changes were made."
                : null;
    return (_jsxs("div", { className: "overflow-hidden rounded-lg border border-gray-300 bg-white", children: [_jsxs("header", { className: "flex h-[38px] items-center gap-1.5 border-b border-gray-300 bg-white px-2 py-0", children: [_jsx("div", { className: "flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-gray-100 p-0.5", children: _jsx(PencilSimple, { size: 18, className: "text-gray-600" }) }), _jsx("h2", { className: "text-[12px] leading-[1.2] font-bold text-gray-600", children: "Slide Operations" }), _jsxs("div", { className: "ml-auto flex items-center gap-2", children: [_jsxs("span", { className: "text-[12px] leading-[1.2] text-gray-600", children: [stats.moves, " move", stats.moves !== 1 ? "s" : "", ", ", stats.deletions, " deletion", stats.deletions !== 1 ? "s" : ""] }), _jsx("button", { type: "button", className: "flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100", onClick: () => setIsExpanded((prev) => !prev), "aria-expanded": isExpanded, "aria-label": isExpanded ? "Collapse slide operations" : "Expand slide operations", children: _jsx(CaretUp, { size: 16, className: isExpanded ? "text-gray-600" : "rotate-180 text-gray-600" }) })] })] }), isExpanded && (_jsxs(_Fragment, { children: [_jsx("section", { className: "px-2 py-3", children: _jsx("div", { className: "space-y-2", children: hydratedOperations.map((operation) => {
                                const decision = approvalDecisions.get(operation.index);
                                const rowStatus = getRowStatus(operation, decision, data.status);
                                const isCollapsed = collapsedRows.has(operation.index);
                                return (_jsx(OperationRow, { operation: operation, decision: decision, rowStatus: rowStatus, isCollapsed: isCollapsed, isDisabled: isDisabled, onToggleCollapse: () => {
                                        setCollapsedRows((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(operation.index))
                                                next.delete(operation.index);
                                            else
                                                next.add(operation.index);
                                            return next;
                                        });
                                    }, onApprove: () => handleApprove(operation.index), onReject: () => handleReject(operation.index), onResetDecision: () => handleResetDecision(operation.index) }, operation.index));
                            }) }) }), stats.deletions > 0 && (_jsxs("footer", { className: "flex items-center gap-3 border-t border-gray-300 bg-white p-3", children: [_jsx("div", { className: "flex flex-1 items-center", children: _jsxs("p", { className: "text-[13px] leading-[1.5] text-gray-900", children: [stats.pendingDeletions, " decision", stats.pendingDeletions !== 1 ? "s" : "", " remaining"] }) }), _jsxs("div", { className: "inline-flex items-center gap-2 self-stretch", children: [_jsx("button", { type: "button", onClick: () => onCancel?.(), disabled: isDisabled, className: "inline-flex items-center justify-center rounded-lg border border-gray-300 bg-transparent px-3 py-[6.5px] text-[13px] leading-[1.2] font-bold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50", children: "Cancel All" }), _jsx("button", { type: "button", onClick: handleSubmit, disabled: isDisabled || !allDeletionsDecided, className: [
                                            "inline-flex items-center justify-center rounded-lg px-3 py-[6.5px] text-[13px] leading-[1.2] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                            isDisabled || !allDeletionsDecided
                                                ? "bg-gray-300 text-gray-600"
                                                : "bg-[#6050d7] text-white hover:bg-[#5344c6]",
                                        ].join(" "), children: "Continue" })] })] })), statusMessage && (_jsx("div", { className: "border-t border-gray-300 bg-gray-50 px-3 py-2 text-[12px] leading-[1.5] text-gray-700", children: statusMessage }))] }))] }));
};
export default SlideOperationsApprovalBlock;
